import { cn } from "@/lib/utils.ts";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { CommandLoading } from "cmdk";
import { useGetMyMentorships } from "@/lib/api/mentorships.ts";
import { getMenteeDisplayName } from "@/lib/redacted-display";

interface MyMenteesSelectProps {
  menteeIds: string[];
  handleFilterChange: (key: string, value: string[]) => void;
}

const encodeValue = (value: string, keywords: string[] = []) =>
  `${keywords.join("|")}|${value}`;
// const decodeValue = (value: string) => value.split("|").at(-1) as string;

export function MyMenteesSelect({
  menteeIds,
  handleFilterChange,
}: MyMenteesSelectProps) {
  const [open, setOpen] = useState(false);
  const { data: mentorships, isPending } = useGetMyMentorships();

  const myMentees = useMemo(
    () => mentorships?.flatMap((m) => (m.mentee?.id ? [m.mentee] : [])),
    [mentorships],
  );

  const toggleMentee = (menteeId: string) => {
    const newMenteeIds = menteeIds.includes(menteeId)
      ? menteeIds.filter((id) => id !== menteeId)
      : [...menteeIds, menteeId];
    handleFilterChange("menteeIds", newMenteeIds);
  };

  const buttonText = useMemo(() => {
    const menteeIdsWithoutAll = menteeIds.filter((id) => id !== "all");
    if (menteeIdsWithoutAll.length === 0) {
      return "All Teens";
    } else if (menteeIdsWithoutAll.length === 1) {
      const selected = myMentees?.find(
        (mentee) => mentee.id === menteeIds[0],
      );
      return selected ? getMenteeDisplayName(selected) : undefined;
    } else {
      return `${menteeIdsWithoutAll.length} Teens`;
    }
  }, [menteeIds, myMentees]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {buttonText}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder="Search teen..." />

          <CommandList>
            <CommandEmpty>No teens found.</CommandEmpty>
            {isPending && <CommandLoading>Hang on…</CommandLoading>}

            <CommandGroup>
              <CommandItem
                value="all"
                keywords={["all"]}
                onSelect={() => {
                  handleFilterChange("menteeIds", []);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    menteeIds.length === 0 ? "opacity-100" : "opacity-0",
                  )}
                />
                All Teens
              </CommandItem>
              {myMentees?.map((mentee) => {
                const displayName = getMenteeDisplayName(mentee);
                return (
                  <CommandItem
                    key={mentee.id}
                    value={encodeValue(mentee.id, [displayName])}
                    keywords={[displayName]}
                    onSelect={() => {
                      toggleMentee(mentee.id);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        menteeIds.includes(mentee.id)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {displayName}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
